// Centralized pricing engine for all service categories
import { SQFT_OPTIONS } from '../data/constants';

/**
 * Get home square footage from the selected option
 * @param {string} sqftValue - The sqft option value
 * @returns {number} - The estimated square footage
 */
export const getHomeSqft = (sqftValue) => {
  const sqftOption = SQFT_OPTIONS.find(opt => opt.value === sqftValue);
  return sqftOption ? sqftOption.sqft : 1500; // default 1500
};

/**
 * Calculate total price range from an array of price objects
 * @param {Array<{low: number, high: number}>} prices - Array of price ranges
 * @returns {{low: number, high: number}} - Combined price range
 */
export const sumPriceRanges = (prices) => {
  return prices.reduce(
    (acc, price) => ({
      low: acc.low + (price?.low || 0),
      high: acc.high + (price?.high || 0)
    }),
    { low: 0, high: 0 }
  );
};

/**
 * Apply a multiplier to a price range
 * @param {{low: number, high: number}} price - Price range
 * @param {number} multiplier - Multiplier to apply
 * @returns {{low: number, high: number}} - Adjusted price range
 */
export const applyMultiplier = (price, multiplier) => {
  return {
    low: Math.round(price.low * multiplier),
    high: Math.round(price.high * multiplier)
  };
};

/**
 * Calculate per-sqft pricing
 * @param {{low: number, high: number}} pricePerSqft - Price per square foot
 * @param {number} sqft - Total square footage
 * @returns {{low: number, high: number}} - Total price range
 */
export const calculatePerSqftPrice = (pricePerSqft, sqft) => {
  return {
    low: Math.round(pricePerSqft.low * sqft),
    high: Math.round(pricePerSqft.high * sqft)
  };
};

/**
 * Calculate price based on quantity selection
 * @param {{low: number, high: number}} unitPrice - Price per unit
 * @param {string} quantityValue - Quantity selection value (e.g., "1", "2-3", "4-6", "7+")
 * @returns {{low: number, high: number}} - Total price range
 */
export const calculateQuantityPrice = (unitPrice, quantityValue) => {
  const quantityMap = {
    "1": 1,
    "2": 2,
    "3": 3,
    "1-2": 1.5,
    "2-3": 2.5,
    "3-5": 4,
    "4-6": 5,
    "6+": 7,
    "7+": 8,
    "1-3": 2,
    "4+": 5,
    "1-4": 2.5,
    "5-8": 6.5,
    "9-12": 10.5,
    "12+": 14
  };
  
  const multiplier = quantityMap[quantityValue] || 1;
  return applyMultiplier(unitPrice, multiplier);
};

/**
 * Get price from a service by value
 * @param {Array} services - Array of service objects with value and price
 * @param {string} value - Service value to look up
 * @returns {{low: number, high: number}|null} - Price range or null
 */
export const getServicePrice = (services, value) => {
  const service = services.find(s => s.value === value);
  return service?.price || null;
};

/**
 * Calculate prices for selected services from collapsible categories
 * @param {Array} categories - Array of category objects with services
 * @param {Array<string>} selectedServices - Array of selected service values
 * @returns {{low: number, high: number}} - Total price range
 */
export const calculateCollapsibleCategoriesPrice = (categories, selectedServices) => {
  const prices = [];
  
  selectedServices.forEach(serviceValue => {
    for (const category of categories) {
      const service = category.services.find(s => s.value === serviceValue);
      if (service?.price) {
        prices.push(service.price);
        break;
      }
    }
  });
  
  return sumPriceRanges(prices);
};

/**
 * Format price for display
 * @param {number} price - Price to format
 * @returns {string} - Formatted price string
 */
export const formatPrice = (price) => {
  if (price >= 1000) {
    return `$${(price / 1000).toFixed(1)}k`.replace('.0k', 'k');
  }
  return `$${price.toLocaleString()}`;
};

/**
 * Format price range for display
 * @param {{low: number, high: number}} range - Price range
 * @returns {string} - Formatted price range string
 */
export const formatPriceRange = (range) => {
  if (!range || (range.low === 0 && range.high === 0)) {
    return null;
  }
  return `${formatPrice(range.low)} – ${formatPrice(range.high)}`;
};
